// @flow

// $FlowFixMe[cannot-resolve-module] -- modernizr doesn't have types
import Modernizr from 'modernizr';
import { toQueryString } from 'common/api/apiClient';

/**
 * Escapes only the characters that would end a key or value early once the
 * string reaches the address bar. Everything else -- Unicode, `,`, `[]` --
 * stays literal, so a pushed URL reads `?vt=1,2&location=Töölö`.
 */
function escapeDelimiters(value: string): string {
  return value
    .replace(
      /[%&#=+]/g,
      character => `%${character.charCodeAt(0).toString(16).toUpperCase()}`
    )
    .replace(/ /g, '+');
}

/**
 * Serializes params for display: percent-decoded for legibility, with the
 * delimiters put back so the URL still parses to the values it was built
 * from. A venue title of `Bar & Grill` has to survive a reload.
 */
function toReadableQueryString(params: Object): string {
  const pairs = [];

  new URLSearchParams(toQueryString(params)).forEach((value, key) => {
    pairs.push(`${escapeDelimiters(key)}=${escapeDelimiters(value)}`);
  });

  return pairs.join('&');
}

/**
 * Pushes the given results and search parameters to History API and changes the
 * URL to match those parameters.
 *
 * @param {Object} state - The new state to be set
 * @param {Object} state.params - The corresponding parameters for the state
 */
export function pushState(state: any) {
  if (!Modernizr.history) return;

  const queryParams = toReadableQueryString(state.params || {});
  let url = window.location.pathname;
  if (queryParams) {
    url += `?${queryParams}`;
  }

  window.history.pushState(state, null, url);
}

/**
 * Replaces the current History API state to match the given values.
 *
 * @param {Object} newState - The state to be set
 * @param {Object} newState.params - The corresponding parameters for the state
 */
export function replaceState(newState: any) {
  if (!Modernizr.history) return;

  window.history.replaceState(newState, null, null);
}

/**
 * Hooks the given callback to History API popstate events.
 *
 * @param {popStateCallback} callback - The callback that handles the new state.
 * @returns {function} Removes the listener.
 */
export function onPopState(callback: (state: any) => mixed): () => void {
  const handlePopState = (event: { +state: any, ... }) => {
    const state = event.state;

    // An entry a same-page `#` link adds carries a null state, and one pushed
    // by anything else on the page carries no `params`. Neither holds search
    // state to restore, and passing one on throws downstream.
    if (!state || typeof state.params !== 'object' || state.params === null) {
      return;
    }

    callback(state);
  };

  window.addEventListener('popstate', handlePopState);

  return () => window.removeEventListener('popstate', handlePopState);
}

/**
 * A callback handling a new state from popState event
 * @callback popStateCallback
 *
 * @param {Object} state - The state received from popState event
 * @param {Object} state.params - The corresponding parameters for the state
 */
